import { useCallback, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { useToast } from "../providers/ToastProvider";

type FeedbackSetter = Dispatch<SetStateAction<string | null>>;

function resolveNext(
  action: SetStateAction<string | null>,
  previous: string | null
): string | null {
  return typeof action === "function" ? action(previous) : action;
}

interface FeedbackOptions {
  toast?: boolean;
}

export function useFormFeedback(options?: FeedbackOptions) {
  const toast = useToast();
  const toastEnabled = options?.toast ?? true;
  const [error, setErrorState] = useState<string | null>(null);
  const [success, setSuccessState] = useState<string | null>(null);
  const lastErrorToast = useRef<string | null>(null);
  const lastSuccessToast = useRef<string | null>(null);

  const setError: FeedbackSetter = useCallback(
    (action) => {
      setErrorState((prev) => {
        const next = resolveNext(action, prev);
        if (next && next !== prev) {
          if (toastEnabled && lastErrorToast.current !== next) {
            queueMicrotask(() => toast.error(next));
            lastErrorToast.current = next;
          }
          setSuccessState(null);
          lastSuccessToast.current = null;
        } else if (!next) {
          lastErrorToast.current = null;
        }
        return next;
      });
    },
    [toast, toastEnabled]
  );

  const setSuccess: FeedbackSetter = useCallback(
    (action) => {
      setSuccessState((prev) => {
        const next = resolveNext(action, prev);
        if (next && next !== prev) {
          if (toastEnabled && lastSuccessToast.current !== next) {
            queueMicrotask(() => toast.success(next));
            lastSuccessToast.current = next;
          }
          setErrorState(null);
          lastErrorToast.current = null;
        } else if (!next) {
          lastSuccessToast.current = null;
        }
        return next;
      });
    },
    [toast, toastEnabled]
  );

  const resetFeedback = useCallback(() => {
    setErrorState(null);
    setSuccessState(null);
    lastErrorToast.current = null;
    lastSuccessToast.current = null;
  }, []);

  return {
    error,
    success,
    setError,
    setSuccess,
    resetFeedback
  };
}
